import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react'

import { DESCRIPTIONS } from '@/constants/common/message.constants'
import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'

import { DatePicker } from '../DatePicker'
const { TimePicker } = DatePicker

type BaseTPProps = React.ComponentProps<typeof TimePicker>
type NormalizedTimePickerProps = Omit<BaseTPProps, 'value' | 'onChange'> & {
  value?: Date | null
  onChange?: (date: Date | null, dateString: string | string[]) => void
  onInvalidText?: (message: string) => void
  onValidText?: () => void
  /**
   * value が空のときに時刻を載せる基準日。
   * 時刻のみ表示する用途で、未指定だと操作当日の日付が付いてしまい、
   * 過去日の実績を後日入力すると日付部分だけズレて保存される。
   * value があるときは value の日付を維持する（基準日で上書きしない）。
   */
  baseDate?: Date | null
}

/** "HH:mm" 表示における時 / 分セグメントのキャレット範囲 */
const SEGMENT_RANGE = { hour: [0, 2], minute: [3, 5] } as const

/** 全角→半角 & 空白除去 */
const toHalfWidth = (s: string) => s.normalize('NFKC').replace(/\s+/g, '')

// "HH:mm" or "HHmm"(4桁) のみ許可
function normalizeToHHmm(raw: string): string | null {
  const s = toHalfWidth(raw).trim()
  let m = s.match(/^([01]\d|2[0-3]):([0-5]\d)$/)
  if (m) return `${m[1]}:${m[2]}`
  m = s.match(/^(\d{4})$/)
  if (m) {
    const hh = m[1].slice(0, 2)
    const mm = m[1].slice(2, 4)
    if (/^([01]\d|2[0-3])$/.test(hh) && /^[0-5]\d$/.test(mm)) {
      return `${hh}:${mm}`
    }
  }
  return null
}

function safeParseISO(v: unknown): Date | null {
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v
  if (typeof v === 'string' && v) {
    const d = new Date(v)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

function applyTimeToBase(
  value: unknown,
  hhmm: string,
  baseDate?: Date | null
): Date {
  const base = safeParseISO(value) ?? safeParseISO(baseDate) ?? new Date()
  const [h, m] = hhmm.split(':').map((v) => parseInt(v, 10))
  const d = new Date(base)
  d.setHours(h, m, 0, 0)
  return d
}

function isAllowedByDisabledTime(
  d: Date,
  disabledTime?: BaseTPProps['disabledTime']
) {
  if (!disabledTime) return true
  const cfg = disabledTime(d) || {}
  const h = d.getHours()
  const m = d.getMinutes()
  if (cfg.disabledHours && cfg.disabledHours().includes(h)) return false
  if (cfg.disabledMinutes) {
    const arr = cfg.disabledMinutes(h) || []
    if (arr.includes(m)) return false
  }
  return true
}

type DayjsLike = { toDate: () => Date }

const isDayjsLike = (v: unknown): v is DayjsLike => {
  return (
    typeof v === 'object' &&
    v !== null &&
    'toDate' in v &&
    typeof (v as { toDate: unknown }).toDate === 'function'
  )
}

const toDate = (v: unknown): Date | null => {
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v
  if (isDayjsLike(v)) {
    const d = v.toDate()
    return d instanceof Date && !isNaN(d.getTime()) ? d : null
  }
  return null
}

export function NormalizedTimePicker(props: NormalizedTimePickerProps) {
  const {
    value,
    onChange,
    format = 'HH:mm',
    disabledTime,
    onInvalidText,
    onValidText,
    placeholder = DESCRIPTIONS.SELECT_OCLOCK,
    minuteStep,
    baseDate,
    ...rest
  } = props

  // ↑↓キーでの増減幅。分は minuteStep に従う（未指定時は 1 分）
  const step = typeof minuteStep === 'number' && minuteStep > 0 ? minuteStep : 1

  // ↑↓キー編集中に対象セグメント（時 / 分）を固定する。
  // 値変更のたびにブラウザ / rc-picker がキャレットを末尾へ動かすため、
  // 生のキャレット位置から毎回セグメントを判定すると連続操作で破綻する。
  // クリック / 左右キー / blur でリセットし、次の ↑↓ で再判定する。
  const activeSegmentRef = useRef<'hour' | 'minute' | null>(null)
  const inputElRef = useRef<HTMLInputElement | null>(null)

  // 値変更に伴う再レンダー後、編集中セグメントへ即キャレットを戻す（同期）。
  useLayoutEffect(() => {
    const seg = activeSegmentRef.current
    const el = inputElRef.current
    if (!seg || !el) return
    const [start, end] = SEGMENT_RANGE[seg]
    el.setSelectionRange(start, end)
  })

  // rc-picker は値変更後に不定のタイミングで input.value を命令的に再代入し
  // （再レンダーを伴わず）キャレットを末尾へ飛ばす。タイミング依存の rAF では
  // 取りこぼすため、selectionchange を監視して ↑↓ 編集中に選択位置がセグメント
  // から逸れたら即座に戻す。既に一致していれば何もしないので再帰ループしない。
  useEffect(() => {
    const handleSelectionChange = () => {
      const seg = activeSegmentRef.current
      const el = inputElRef.current
      if (!seg || !el || document.activeElement !== el) return
      const [start, end] = SEGMENT_RANGE[seg]
      if (el.selectionStart !== start || el.selectionEnd !== end) {
        el.setSelectionRange(start, end)
      }
    }
    document.addEventListener('selectionchange', handleSelectionChange)
    return () =>
      document.removeEventListener('selectionchange', handleSelectionChange)
  }, [])

  const isPointerDownOnTimePanel = useRef(false)

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      // マウス操作が入ったら ↑↓ のセグメント固定を解除し、
      // 次の ↑↓ でキャレット位置（＝クリック位置）から再判定させる
      activeSegmentRef.current = null
      const target = event.target as HTMLElement | null
      if (target?.closest('.ant-picker-time-panel-cell-inner')) {
        isPointerDownOnTimePanel.current = true
      }
    }
    const handlePointerUp = () => {
      isPointerDownOnTimePanel.current = false
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    document.addEventListener('pointerup', handlePointerUp, true)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
      document.removeEventListener('pointerup', handlePointerUp, true)
    }
  }, [])

  const handleChange = useMemo(() => {
    return (date: unknown, dateString: string | string[]) => {
      const picked = toDate(date)
      // パネル選択時、antd は value が空だと当日基準の Date を返すため、
      // 時刻だけ取り出して基準日に載せ直す
      const d =
        picked && !safeParseISO(value) && safeParseISO(baseDate)
          ? applyTimeToBase(
              null,
              `${picked.getHours()}:${picked.getMinutes()}`,
              baseDate
            )
          : picked
      if (d) {
        onValidText?.()
        onChange?.(d, typeof dateString === 'string' ? dateString : '')
      } else {
        onChange?.(null, typeof dateString === 'string' ? dateString : '')
      }
    }
  }, [onChange, onValidText, value, baseDate])

  const handleBlur: React.FocusEventHandler<HTMLElement> = (e) => {
    if (isPointerDownOnTimePanel.current) {
      // 時刻選択パネルのセルをクリック中に blur した場合は無視
      return
    }

    // フォーカスを外れたら ↑↓ のセグメント固定を解除する
    activeSegmentRef.current = null

    const maybeInputFromTarget = e.target as unknown as HTMLInputElement | null
    const inputEl =
      (maybeInputFromTarget && 'value' in maybeInputFromTarget
        ? maybeInputFromTarget
        : null) || (e.currentTarget as HTMLElement).querySelector('input')

    const raw = (inputEl?.value ?? '').trim()
    if (!raw) return

    const hhmm = normalizeToHHmm(raw)
    if (!hhmm) {
      if (inputEl) inputEl.value = ''
      onInvalidText?.(VALIDATION_MESSAGES.TIME_FORMAT_HHMM)
      return
    }

    const next = applyTimeToBase(value, hhmm, baseDate)

    if (!isAllowedByDisabledTime(next, disabledTime)) {
      if (inputEl) inputEl.value = ''
      onInvalidText?.(VALIDATION_MESSAGES.INVALID_TIME_SELECTION)
      return
    }

    if (inputEl) inputEl.value = hhmm
    onChange?.(next, hhmm)
    onValidText?.()
  }

  const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (e) => {
    if (e.key === 'Enter') {
      // Enter submit 前に正規化を実行
      handleBlur(e as unknown as React.FocusEvent<HTMLElement>)
      return
    }

    // rc-picker は input 要素の keydown でこのハンドラを呼ぶため、
    // target / currentTarget が input 自身になる。子孫探索では取れない
    const target = e.target as HTMLElement
    const current = e.currentTarget as HTMLElement
    const inputEl =
      (target instanceof HTMLInputElement ? target : null) ??
      (current instanceof HTMLInputElement
        ? current
        : current.querySelector('input'))
    inputElRef.current = inputEl

    // ↑↓キーで、キャレット位置のセグメント（時 / 分）を増減する。
    // パネルを開かずキーボードだけで値を選べるようにする。
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if (!inputEl) return

      // rc-picker 側のデフォルト挙動（キャレット移動・パネル操作）は抑止する
      e.preventDefault()

      // 編集中セグメントは固定する。連続操作で値変更のたびにキャレットが
      // 末尾へ動いても判定がぶれないよう、初回のみキャレット位置から決める。
      // "HH:mm" の ':' は index 2。それ以前を時、以降を分とみなす
      if (!activeSegmentRef.current) {
        const caret = inputEl.selectionStart ?? 0
        activeSegmentRef.current = caret <= 2 ? 'hour' : 'minute'
      }
      const editingHour = activeSegmentRef.current === 'hour'
      // プルダウン（昇順リスト）の並びに合わせ、↓=次（増加・リストを下へ）、
      // ↑=前（減少・リストを上へ）とする
      const dir = e.key === 'ArrowUp' ? -1 : 1

      const base = safeParseISO(value)
      let h = base ? base.getHours() : 0
      let m = base ? base.getMinutes() : 0
      if (editingHour) {
        h = (((h + dir) % 24) + 24) % 24
      } else {
        // step 境界にスナップしてから増減
        const snapped = Math.round(m / step) * step
        m = (((snapped + dir * step) % 60) + 60) % 60
      }

      const hhmm = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
      const next = applyTimeToBase(value, hhmm, baseDate)
      onChange?.(next, hhmm)
      onValidText?.()
      // キャレットの復元は再レンダー後の useLayoutEffect が担う
      return
    }

    // ↑↓以外のキー操作（左右移動・文字入力等）はセグメント固定を解除し、
    // 次の ↑↓ でキャレット位置から再判定させる
    activeSegmentRef.current = null
  }

  return (
    <TimePicker
      {...rest}
      placeholder={placeholder}
      format={format}
      minuteStep={minuteStep}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      disabledTime={disabledTime}
      onKeyDown={handleKeyDown}
    />
  )
}
